import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Emblem } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";

const CHEF_TYPES = [
  { v: "Chef", d: "Solo creation & recipe development" },
  { v: "Sous Chef", d: "Advanced PCA, purchasing & inventory" },
  { v: "Executive Chef", d: "Full command center, team & analytics" },
  { v: "Master Chef", d: "Multi-location & complete control" },
];
const RESTAURANT_TYPES = ["Fine Dining", "Casual Dining", "Hotel", "Catering", "Private Chef", "Pop-Up", "Food Truck", "Banquet"];
const CUISINES = ["Modern American", "French", "Italian", "Japanese", "Southern", "Mediterranean", "Mexican", "Steakhouse", "Vegan", "Fusion"];
const DIETS = ["Vegetarian", "Vegan", "Gluten-Free", "Keto", "Halal", "Kosher", "Nut-Free", "Dairy-Free"];

export default function Onboarding() {
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [d, setD] = useState({
    chef_title: "", restaurant: "", restaurant_type: "Fine Dining", cuisine_specialties: [],
    num_locations: 1, average_covers: 80, measurement_system: "imperial", target_food_cost: 28,
    dietary_specialties: [], supplier_preferences: ["Sysco"],
  });
  const upd = (k, v) => setD({ ...d, [k]: v });
  const toggle = (k, v) => setD({ ...d, [k]: d[k].includes(v) ? d[k].filter((x) => x !== v) : [...d[k], v] });

  const finish = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding", d);
      await refreshUser();
      toast.success("Your kitchen is ready, Chef.");
      nav("/dashboard");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const steps = [
    // 0 profile
    (
      <div className="space-y-5">
        <h2 className="font-serif text-3xl">Tell us about your kitchen</h2>
        <TextField label="Restaurant / Company" value={d.restaurant} onChange={(e) => upd("restaurant", e.target.value)} testid="ob-restaurant" />
        <div>
          <label className="label-eyebrow block mb-2">Restaurant type</label>
          <div className="flex flex-wrap gap-2">
            {RESTAURANT_TYPES.map((t) => <Chip key={t} active={d.restaurant_type === t} onClick={() => upd("restaurant_type", t)} testid={`ob-rtype-${t}`}>{t}</Chip>)}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <TextField label="Locations" type="number" value={d.num_locations} onChange={(e) => upd("num_locations", +e.target.value)} testid="ob-locations" />
          <TextField label="Average covers" type="number" value={d.average_covers} onChange={(e) => upd("average_covers", +e.target.value)} testid="ob-covers" />
        </div>
      </div>
    ),
    // 1 cuisine + diet
    (
      <div className="space-y-6">
        <h2 className="font-serif text-3xl">Your culinary DNA</h2>
        <div>
          <label className="label-eyebrow block mb-2">Cuisine specialties</label>
          <div className="flex flex-wrap gap-2">
            {CUISINES.map((c) => <Chip key={c} active={d.cuisine_specialties.includes(c)} onClick={() => toggle("cuisine_specialties", c)} testid={`ob-cuisine-${c}`}>{c}</Chip>)}
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Dietary specialties</label>
          <div className="flex flex-wrap gap-2">
            {DIETS.map((c) => <Chip key={c} active={d.dietary_specialties.includes(c)} onClick={() => toggle("dietary_specialties", c)} testid={`ob-diet-${c}`}>{c}</Chip>)}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">Measurement</label>
            <div className="flex gap-2">
              {["imperial", "metric"].map((m) => <Chip key={m} active={d.measurement_system === m} onClick={() => upd("measurement_system", m)} testid={`ob-measure-${m}`}>{m}</Chip>)}
            </div>
          </div>
          <TextField label="Target food cost %" type="number" value={d.target_food_cost} onChange={(e) => upd("target_food_cost", +e.target.value)} testid="ob-foodcost" />
        </div>
      </div>
    ),
    // 2 chef type
    (
      <div className="space-y-5">
        <h2 className="font-serif text-3xl">What kind of Chef are you?</h2>
        <p className="text-white/50">This sets your role and access level.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {CHEF_TYPES.map((c) => (
            <button key={c.v} onClick={() => upd("chef_title", c.v)} data-testid={`ob-cheftype-${c.v.replace(/ /g, "-")}`}
              className={`text-left p-5 rounded-sm border transition-all min-h-[48px] ${d.chef_title === c.v ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/25"}`}>
              <p className={`font-serif text-2xl ${d.chef_title === c.v ? "gold-text" : ""}`}>{c.v}</p>
              <p className="text-white/50 text-sm mt-1">{c.d}</p>
            </button>
          ))}
        </div>
      </div>
    ),
  ];

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center p-6 grain">
      <div className="w-full max-w-2xl">
        <div className="flex items-center justify-center gap-2.5 mb-8"><Emblem className="h-9 w-9" />
          <span className="font-serif text-xl gold-text tracking-wider">CHEF PLATED</span></div>
        <div className="flex gap-1.5 mb-8 justify-center">
          {steps.map((_, i) => <div key={i} className={`h-1 w-20 rounded-full ${i <= step ? "bg-gold" : "bg-white/10"}`} />)}
        </div>
        <div className="matte-card p-8">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              {steps[step]}
            </motion.div>
          </AnimatePresence>
          <div className="flex justify-between mt-8 pt-6 border-t border-white/5">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
              className="px-6 py-3 text-sm text-white/50 hover:text-white disabled:opacity-30">Back</button>
            {step < steps.length - 1 ? (
              <button onClick={() => setStep(step + 1)} data-testid="ob-next" className="btn-gold px-8 py-3 text-sm uppercase tracking-widest rounded-sm">Continue</button>
            ) : (
              <button onClick={finish} disabled={saving || !d.chef_title} data-testid="ob-finish" className="btn-gold px-8 py-3 text-sm uppercase tracking-widest rounded-sm">
                {saving ? "Setting up…" : "Enter Command Center"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TextField({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input type={type} value={value} onChange={onChange} data-testid={testid}
        className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3.5 text-white/90 outline-none focus:border-gold/60" />
    </div>
  );
}
function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-4 py-2.5 rounded-sm text-sm border transition-colors ${active ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-white/60 hover:border-white/25"}`}>
      {children}
    </button>
  );
}
